/**
 * Live updates while the app is open.
 *
 * The API's `/api/events` stream carries content-free "something changed"
 * signals (events.service.ts); on each one we refetch letters and
 * conversations through the normal endpoints. Nothing about a letter travels
 * over the stream itself, so peeks, hidden pairs and tiers stay server-side.
 *
 * EventSource reconnects on its own after a drop; whenever it (re)opens we
 * refetch once too, to catch anything that changed while it was down.
 */

import { useEffect } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { API_ORIGIN } from '../../config';
import { lettersApi } from '../letters/api';
import { relationshipsApi } from '../relationships/api';

/** Several changes often land together (a delivery sweep) — refetch once. */
const COALESCE_MS = 400;

export function useLiveUpdates(enabled: boolean) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const refetch = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        dispatch(lettersApi.util.invalidateTags(['Letter', 'Stamps']));
        dispatch(relationshipsApi.util.invalidateTags(['Relationship', 'Bio', 'History']));
      }, COALESCE_MS);
    };

    let opened = false;
    const source = new EventSource(`${API_ORIGIN}/api/events`, { withCredentials: true });
    source.onopen = () => {
      // The first open is the initial load, which just fetched everything.
      if (opened) refetch();
      opened = true;
    };
    source.onmessage = (e) => {
      try {
        if ((JSON.parse(e.data as string) as { type?: string }).type === 'changed') refetch();
      } catch {
        // A malformed frame is not worth a refetch storm.
      }
    };

    return () => {
      source.close();
      if (timer) clearTimeout(timer);
    };
  }, [enabled, dispatch]);
}
